import { Deferred, PriorityQueue } from '@logosdx/utils';
import { EventError } from './helpers.mjs';
export class DeferredEvent extends Deferred {
    cleanup;
}
export class EventGenerator {
    #observer;
    #event;
    #buffer;
    #waiting = null;
    #done = false;
    #listener = null;
    #lastValue = null;
    #assertNotDestroyed = ()=>{
        if (this.#done === true) {
            throw new EventError(`Event generator for ${this.#event.toString()} has been destroyed`, {
                event: this.#event,
                listener: this.#listener,
                data: null
            });
        }
    };
    cleanup;
    next;
    constructor(observer, event, options){
        this.#observer = observer;
        this.#event = event;
        this.#buffer = new PriorityQueue();
        this.#listener = (data)=>{
            this.#lastValue = data;
            if (this.#waiting) {
                const defer = this.#waiting;
                this.#waiting = null;
                defer.resolve(data);
            } else {
                this.#buffer.push(data);
            }
        };
        const off = observer.on(event, this.#listener);
        // Mutable controller for managing abort listener lifecycle
        let generatorAbortCtrl;
        this.next = ()=>{
            this.#assertNotDestroyed();
            const buffered = this.#buffer.pop();
            if (buffered !== null) {
                return Promise.resolve(buffered);
            }
            if (!this.#waiting) {
                this.#waiting = new DeferredEvent();
                this.#waiting.promise.cleanup = this.cleanup;
                this.#waiting.promise.resolve = this.#waiting.resolve;
                this.#waiting.promise.reject = this.#waiting.reject;
            }
            return this.#waiting.promise;
        };
        this.cleanup = ()=>{
            if (this.#done) return;
            off();
            this.#done = true;
            // Reject pending promises on cancellation
            const abortError = new EventError('Aborted', {
                event: this.#event,
                listener: this.#listener,
                data: null
            });
            if (this.#waiting) {
                this.#waiting.reject(abortError);
                this.#waiting = null;
            }
            // Reject all lingering iterator promises
            this.#_iterPromise.forEach((promise)=>{
                promise.reject?.(abortError);
            });
            // Cleanup the set and buffer
            this.#_iterPromise.clear();
            this.#buffer.clear();
            this.#lastValue = null;
            // Abort the generator's abort controller if it exists
            generatorAbortCtrl?.abort();
        };
        // Wire up signal to auto-cleanup
        const userSignal = options?.signal;
        if (userSignal) {
            if (userSignal.aborted) {
                this.#done = true;
                return;
            }
            generatorAbortCtrl = new AbortController();
            userSignal.addEventListener('abort', this.cleanup, {
                signal: generatorAbortCtrl.signal
            });
        }
    }
    /**
     * Store iterator's last promise. Handles multiple promises
     * for when the generator is used in multiple places.
     */ #_iterPromise = new Set();
    async *[Symbol.asyncIterator]() {
        while(!this.#done){
            // capture the promise before yielding
            const promise = this.next();
            this.#_iterPromise.add(promise);
            yield await promise;
            // remove the promise after yielding
            this.#_iterPromise.delete(promise);
        }
        return Promise.resolve(undefined);
    }
    get lastValue() {
        return this.#lastValue;
    }
    get done() {
        return this.#done;
    }
    emit(data) {
        this.#assertNotDestroyed();
        this.#observer.emit(this.#event, data);
    }
}
